function required(document, selector, label) {
  const element = document.querySelector(selector);
  if (!element) throw new Error(`${label} was not found in the delivered page shell`);
  return element;
}

export function createBurgerMenu({ document, onSignIn = null, onCollection = null }) {
  const toolbarRow = required(document, "#toolbar > .account-toolbar-row", "Site toolbar row");
  const toggle = required(document, "#menuToggle", "Menu toggle");
  const backdrop = required(document, ".menu-backdrop", "Menu backdrop");
  const drawer = required(document, "#siteMenu", "Site menu");
  const closeButton = required(document, "#siteMenu .menu-close", "Menu close button");
  const userToggle = required(document, "#userMenuToggle", "User menu toggle");
  const userBackdrop = required(document, ".user-menu-backdrop", "User menu backdrop");
  const userMenu = required(document, "#userMenu", "User menu");
  const userCloseButton = required(document, "#userMenu .user-menu-close", "User menu close button");
  const accountLoading = required(document, "#userMenu .menu-account-loading", "Account loading state");
  const signInButton = required(document, "#userMenu .menu-account-action", "Sign-in action");
  const userButtonSlot = required(document, "#userMenu .menu-user-slot", "User button slot");
  const collectionButton = required(document, "#userMenu .menu-collection-action", "Collection link");
  const indicator = required(document, "#menuFilterIndicator", "Filter indicator");
  const filterToggle = drawer.querySelector("#filterToggle");
  const filterPanel = drawer.querySelector("#filterPanel");
  const sortToggle = drawer.querySelector("#sortToggle");
  const sortPanel = drawer.querySelector("#sortPanel");
  const viewMenu = drawer.querySelector(".view-menu");

  function actionsContainer(panel, panelCloseButton) {
    const existing = panel.querySelector(".site-menu-actions");
    if (existing) return existing;
    const container = document.createElement("div");
    container.className = "site-menu-actions";
    panelCloseButton.before(container);
    container.append(panelCloseButton);
    return container;
  }

  const siteMenuActions = actionsContainer(drawer, closeButton);
  const userMenuActions = actionsContainer(userMenu, userCloseButton);
  drawer.setAttribute("role", "dialog");
  drawer.setAttribute("aria-modal", "true");
  userMenu.setAttribute("role", "dialog");
  userMenu.setAttribute("aria-modal", "true");

  if (!toolbarRow.contains(toggle)) {
    throw new Error("Menu toggle must be delivered inside the site toolbar row");
  }
  if (!toolbarRow.contains(userToggle)) {
    throw new Error("User menu toggle must be delivered inside the site toolbar row");
  }

  let siteOpen = false;
  let userOpen = false;
  let isolatedElements = [];
  let signInHandler = onSignIn;
  let collectionHandler = onCollection;

  const userToggleHome = document.createComment("user-menu-toggle-home");
  const menuToggleHome = document.createComment("catalogue-menu-toggle-home");
  toolbarRow.insertBefore(userToggleHome, userToggle);
  toolbarRow.insertBefore(menuToggleHome, toggle);

  const focusable = (container) => [...container.querySelectorAll("button, [href], input, select, [tabindex]")]
    .filter((element) => !element.disabled
      && !element.hidden
      && !element.closest('[hidden], [aria-hidden="true"]')
      && element.getAttribute("tabindex") !== "-1");

  function restoreBackgroundIsolation() {
    for (const state of isolatedElements) {
      state.element.inert = state.inert;
      if (state.hadInertAttribute) state.element.setAttribute("inert", "");
      else state.element.removeAttribute("inert");
      if (state.ariaHidden == null) state.element.removeAttribute("aria-hidden");
      else state.element.setAttribute("aria-hidden", state.ariaHidden);
    }
    isolatedElements = [];
  }

  function isolateBackground(activePanel, activeBackdrop) {
    restoreBackgroundIsolation();
    isolatedElements = [...document.body.children]
      .filter((element) => element !== activePanel
        && element !== activeBackdrop
        && !element.matches("script, style, link"))
      .map((element) => ({
        element,
        inert: Boolean(element.inert),
        hadInertAttribute: element.hasAttribute("inert"),
        ariaHidden: element.getAttribute("aria-hidden"),
      }));
    for (const { element } of isolatedElements) {
      element.inert = true;
      element.setAttribute("inert", "");
      element.setAttribute("aria-hidden", "true");
    }
  }

  function dockToggles(actions) {
    actions.append(userToggle, toggle);
  }

  function restoreToggles() {
    toolbarRow.insertBefore(userToggle, userToggleHome.nextSibling);
    toolbarRow.insertBefore(toggle, menuToggleHome.nextSibling);
  }

  function resetPanel(panel, panelToggle, closeSelectors, bodyClass) {
    const isOpen = panel?.classList.contains("open")
      || panel?.getAttribute("aria-hidden") === "false"
      || panelToggle?.getAttribute("aria-expanded") === "true"
      || document.body.classList.contains(bodyClass);
    const closeControl = closeSelectors
      .map((selector) => panel?.querySelector(selector))
      .find(Boolean);
    if (isOpen) closeControl?.click();

    panel?.classList.remove("open");
    panel?.setAttribute("aria-hidden", "true");
    panelToggle?.setAttribute("aria-expanded", "false");
    document.body.classList.remove(bodyClass);
  }

  function closeNestedMenus() {
    resetPanel(filterPanel, filterToggle, ["#filterClose", "#filterBackdrop"], "filters-open");
    resetPanel(sortPanel, sortToggle, ["#sortClose", "#sortBackdrop"], "no-scroll");

    const viewButton = viewMenu?.querySelector("#viewBtn");
    const viewPanel = viewMenu?.querySelector("#viewMenu");
    const viewIsOpen = viewPanel?.classList.contains("open")
      || viewPanel?.getAttribute("aria-hidden") === "false"
      || viewButton?.getAttribute("aria-expanded") === "true";
    if (viewIsOpen) viewButton?.click();

    viewPanel?.classList.remove("open");
    viewPanel?.setAttribute("aria-hidden", "true");
    viewButton?.setAttribute("aria-expanded", "false");
  }

  function showPanel(panel) {
    panel.hidden = false;
    if (typeof panel.showModal === "function" && !panel.open) panel.showModal();
  }

  function hidePanel(panel) {
    if (typeof panel.close === "function" && panel.open) panel.close();
    panel.hidden = true;
  }

  function setSiteOpen(next, restoreFocus = true) {
    if (next && userOpen) setUserOpen(false, false);
    if (!next) closeNestedMenus();
    siteOpen = next;
    if (siteOpen) showPanel(drawer);
    else hidePanel(drawer);
    backdrop.hidden = !siteOpen;
    toggle.setAttribute("aria-expanded", String(siteOpen));
    toggle.setAttribute("aria-label", siteOpen ? "Close menu" : "Open menu");
    document.body.classList.toggle("menu-is-open", siteOpen);
    if (siteOpen) {
      dockToggles(siteMenuActions);
      isolateBackground(drawer, backdrop);
      focusable(drawer)[0]?.focus();
    } else {
      restoreBackgroundIsolation();
      restoreToggles();
      if (restoreFocus) toggle.focus();
    }
  }

  function setUserOpen(next, restoreFocus = true) {
    if (next && siteOpen) setSiteOpen(false, false);
    userOpen = next;
    if (userOpen) showPanel(userMenu);
    else hidePanel(userMenu);
    userBackdrop.hidden = !userOpen;
    userToggle.setAttribute("aria-expanded", String(userOpen));
    userToggle.setAttribute("aria-label", userOpen ? "Close user menu" : "Open user menu");
    document.body.classList.toggle("user-menu-is-open", userOpen);
    if (userOpen) {
      dockToggles(userMenuActions);
      isolateBackground(userMenu, userBackdrop);
      focusable(userMenu)[0]?.focus();
    } else {
      restoreBackgroundIsolation();
      restoreToggles();
      if (restoreFocus) userToggle.focus();
    }
  }

  function filtersAreActive() {
    const controls = [...document.querySelectorAll("#filterPanel input, #filterPanel select")];
    return controls.some((control) => {
      if (control.type === "checkbox" || control.type === "radio") return control.checked;
      return String(control.value ?? "").trim() !== "";
    }) || document.querySelector("#clearFiltersTop")?.disabled === false;
  }

  function updateFilterIndicator() {
    indicator.hidden = !filtersAreActive();
  }

  function setAuthState({ ready, signedIn, unavailable = false }) {
    accountLoading.hidden = ready || unavailable;
    signInButton.hidden = signedIn || (!ready && !unavailable);
    signInButton.disabled = unavailable;
    signInButton.textContent = unavailable ? "Sign in unavailable" : "Sign in or create account";
    userButtonSlot.hidden = !ready || !signedIn;
    collectionButton.hidden = false;
  }

  function bindInlinePanel(panelToggle, panel, closeSelector, bodyClass) {
    if (!panelToggle || !panel) return;
    const setPanelOpen = (next) => {
      panel.classList.toggle("open", next);
      panel.setAttribute("aria-hidden", String(!next));
      panelToggle.setAttribute("aria-expanded", String(next));
      document.body.classList.toggle(bodyClass, next);
    };
    panelToggle.addEventListener("click", () => {
      setPanelOpen(panelToggle.getAttribute("aria-expanded") !== "true");
    });
    panel.querySelector(closeSelector)?.addEventListener("click", () => setPanelOpen(false));
  }

  if (document.body.dataset.resultsRenderer === "client") {
    bindInlinePanel(filterToggle, filterPanel, "#filterClose", "filters-open");
    bindInlinePanel(sortToggle, sortPanel, "#sortClose", "no-scroll");
    const viewButton = viewMenu?.querySelector("#viewBtn");
    const viewPanel = viewMenu?.querySelector("#viewMenu");
    viewButton?.addEventListener("click", () => {
      const next = viewButton.getAttribute("aria-expanded") !== "true";
      viewButton.setAttribute("aria-expanded", String(next));
      viewPanel?.classList.toggle("open", next);
      viewPanel?.setAttribute("aria-hidden", String(!next));
    });
  }

  toggle.addEventListener("click", () => setSiteOpen(!siteOpen));
  closeButton.addEventListener("click", () => setSiteOpen(false));
  backdrop.addEventListener("click", () => setSiteOpen(false));
  userToggle.addEventListener("click", () => setUserOpen(!userOpen));
  userCloseButton.addEventListener("click", () => setUserOpen(false));
  userBackdrop.addEventListener("click", () => setUserOpen(false));
  drawer.addEventListener("cancel", (event) => {
    event.preventDefault();
    setSiteOpen(false);
  });
  userMenu.addEventListener("cancel", (event) => {
    event.preventDefault();
    setUserOpen(false);
  });
  drawer.addEventListener("click", (event) => {
    if (event.target !== drawer || !drawer.open) return;
    const bounds = drawer.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right
      || event.clientY < bounds.top || event.clientY > bounds.bottom) setSiteOpen(false);
  });
  userMenu.addEventListener("click", (event) => {
    if (event.target !== userMenu || !userMenu.open) return;
    const bounds = userMenu.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right
      || event.clientY < bounds.top || event.clientY > bounds.bottom) setUserOpen(false);
  });
  signInButton.addEventListener("click", () => {
    setUserOpen(false, false);
    signInHandler?.();
  });
  collectionButton.addEventListener("click", (event) => {
    setUserOpen(false, false);
    if (collectionHandler) {
      event.preventDefault();
      collectionHandler();
    }
  });
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    if (userOpen) {
      event.preventDefault();
      setUserOpen(false);
    } else if (siteOpen) {
      event.preventDefault();
      setSiteOpen(false);
    }
  });
  function trapFocus(container, event) {
    if (event.key !== "Tab") return;
    const items = focusable(container);
    if (!items.length) return;
    if (!event.shiftKey && document.activeElement === items.at(-1)) {
      event.preventDefault();
      items[0].focus();
    } else if (event.shiftKey && document.activeElement === items[0]) {
      event.preventDefault();
      items.at(-1).focus();
    }
  }
  drawer.addEventListener("keydown", (event) => trapFocus(drawer, event));
  userMenu.addEventListener("keydown", (event) => trapFocus(userMenu, event));
  document.addEventListener("input", updateFilterIndicator);
  document.addEventListener("change", updateFilterIndicator);
  document.querySelector("#clearFiltersTop")?.addEventListener("click", () => queueMicrotask(updateFilterIndicator));
  updateFilterIndicator();
  setAuthState({ ready: false, signedIn: false });

  return {
    collectionButton,
    userButtonSlot,
    open: () => setSiteOpen(true),
    close: () => setSiteOpen(false),
    setSignInHandler(handler) { signInHandler = handler; },
    setCollectionHandler(handler) { collectionHandler = handler; },
    setAuthState,
    setSignedIn(signedIn) { setAuthState({ ready: true, signedIn }); },
    setAuthUnavailable() { setAuthState({ ready: true, signedIn: false, unavailable: true }); },
  };
}
